import { useEffect, useState } from 'react';
import Admin from '../../pages/admin/page';
import Button from '../base/Button';
import Card from '../base/Card';
import Input from '../base/Input';
import { supabase } from '../../utils/supabase';

const AUTHORIZED_ADMIN_EMAIL = 'qiuyating.888@163.com';

type AuthStatus = 'checking' | 'authenticated' | 'anonymous';

type AuthListener = {
  subscription: {
    unsubscribe: () => void;
  };
};

export default function SecureAdminRoute() {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [email, setEmail] = useState(AUTHORIZED_ADMIN_EMAIL);
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const grantAdminAccess = (adminEmail: string) => {
    localStorage.setItem('admin_token', 'supabase_auth');
    localStorage.setItem('admin_email', adminEmail);
    setStatus('authenticated');
    setMessage('');
  };

  const revokeAdminAccess = async () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    await supabase?.auth.signOut();
    setStatus('anonymous');
  };

  useEffect(() => {
    let isMounted = true;
    let authListener: AuthListener | null = null;

    const checkSession = async () => {
      if (!supabase) {
        if (!isMounted) return;
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_email');
        setStatus('anonymous');
        setMessage('Supabase 未连接，请检查环境变量配置。');
        return;
      }

      const { data } = await supabase.auth.getSession();
      const sessionEmail = data.session?.user.email?.toLowerCase();

      if (!isMounted) return;

      if (sessionEmail === AUTHORIZED_ADMIN_EMAIL) {
        grantAdminAccess(sessionEmail);
        return;
      }

      if (data.session) {
        await supabase.auth.signOut();
      }
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_email');
      setStatus('anonymous');
    };

    void checkSession();

    if (supabase) {
      const { data } = supabase.auth.onAuthStateChange((_event, session) => {
        const sessionEmail = session?.user.email?.toLowerCase();
        if (sessionEmail === AUTHORIZED_ADMIN_EMAIL) {
          grantAdminAccess(sessionEmail);
        } else {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_email');
          setStatus('anonymous');
        }
      });
      authListener = data;
    }

    return () => {
      isMounted = false;
      authListener?.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (status !== 'authenticated') return;

    const timer = window.setInterval(() => {
      if (!localStorage.getItem('admin_token')) {
        void revokeAdminAccess();
      }
    }, 250);

    return () => window.clearInterval(timer);
  }, [status]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');

    if (!supabase) {
      setMessage('Supabase 未连接，请检查环境变量配置。');
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (normalizedEmail !== AUTHORIZED_ADMIN_EMAIL) {
      setMessage('请使用授权管理员邮箱登录。');
      return;
    }

    setIsSubmitting(true);
    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });
    setIsSubmitting(false);

    if (error || !data.session) {
      setMessage('邮箱或密码错误。');
      return;
    }

    setPassword('');
    grantAdminAccess(normalizedEmail);
  };

  if (status === 'authenticated') {
    return <Admin />;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #64748b, #cbd5e1)' }}
    >
      <Card className="w-full max-w-md p-8" glass>
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-shield-keyhole-line text-white text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">管理员登录</h1>
          <p className="text-gray-600">请使用 Supabase Auth 管理员账号登录</p>
        </div>

        {status === 'checking' ? (
          <div className="text-center text-gray-600">
            <i className="ri-loader-4-line animate-spin mr-2"></i>
            正在检查登录状态...
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="管理员邮箱"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={AUTHORIZED_ADMIN_EMAIL}
              required
            />
            <Input
              label="Supabase Auth 密码"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="请输入 Supabase Auth 密码"
              required
            />
            {message && <p className="text-sm text-red-600">{message}</p>}
            <Button type="submit" variant="primary" className="w-full whitespace-nowrap" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  登录中...
                </>
              ) : (
                '登录'
              )}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-gray-500">
          密码只由 Supabase Auth 管理，不会写入网站代码
        </div>
      </Card>
    </div>
  );
}
